import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  ThemeProvider,
  CssBaseline,
  IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ClientesTab from './Clientes/ClientesTab';
import MensagensTab from './Mensagens/MensagensTab';
import BrindesTab from './BrindesTab';
import ConfiguracoesTab from './ConfiguracoesTab';
import DashboardTab from './DashboardTab';
import HistoricoTab from './HistoricoTab';
import themeClube from './themeClube';
import OnboardingTour from './OnboardingTour';
import api from '../../../services/api';

const buildOnboardingSteps = (hasLocalidade) => {
  const steps = [
    {
      id: 'intro',
      title: 'Primeiro acesso ao Clube de Mesa',
      description: hasLocalidade
        ? 'Vamos revisar brindes e mensagens para ativar o seu clube.'
        : 'Vamos configurar o essencial em poucos passos.',
      panelPosition: 'bottom-left',
    },
  ];

  steps.push({
    id: 'localidade',
    title: 'Localização e horários de funcionamento',
    description: [
      'Aqui você ajusta a localização do restaurante e os horários de funcionamento.',
      'Use a busca por endereço para preencher latitude e longitude automaticamente.',
      'Informe o raio, o ideal é entre 80 e 100 metros.',
      'Informe o intervalo entre check-ins (h). Depois clique em salvar.',
    ],
    tab: 5,
    target: '[data-tour="config-modal-paper"]',
    openConfigModal: true,
    scrollIntoView: true,
    panelPosition: 'bottom-left',
    padding: 0,
  });

  steps.push({
    id: 'brindes',
    title: 'Cadastre brindes',
    description: [
      'Crie recompensas para seus clientes, como um "vale sobremesa".',
      'Os brindes podem ser associados aos níveis de fidelidade.',
    ],
    tab: 3,
    target: '[data-tour="brindes-novo-button"]',
    targetModal: '[data-tour="brindes-modal-paper"]',
    scrollIntoView: true,
    panelPosition: 'bottom-left',
    padding: 0,
  });

  steps.push({
    id: 'mensagens',
    title: 'Níveis e mensagens automáticas',
    description: [
      'Os níveis representam marcos de fidelidade por número de check-ins.',
      'Por padrão há 3 níveis editáveis. O nível épico não pode ser excluído.',
      'Depois de criar ou editar um nível, clique em "Nova Mensagem" para montar os diálogos.',
    ],
    tab: 2,
    target: '[data-tour="niveis-novo-button"]',
    targetModal: '[data-tour="niveis-modal-paper"]',
    scrollIntoView: true,
    panelPosition: 'bottom-left',
    padding: 0,
  });

  steps.push({
    id: 'mensagens-primeiro-nivel',
    title: 'Mensagens do primeiro ní­vel',
    description: [
      'Aqui você vê o primeiro ní­vel expandido com o fluxo de mensagens.',
      'Você pode adicionar e editar mensagens conforme a conversa.',
      'Cada mensagem pode ser um texto explicativo ou uma pergunta.',
      'Quando é pergunta, o sistema pausa e aguarda a resposta do cliente.',
    ],
    tab: 2,
    target: '[data-tour="nivel-primeiro-card"]',
    scrollIntoView: true,
    panelPosition: 'bottom-left',
  });

  steps.push({
    id: 'mensagens-epico-recorrencia',
    title: 'Nível final e recorrência',
    description: [
      'No nível épico, a primeira mensagem é enviada quando o cliente chega ao nível final.',
      'Depois, a recorrência funciona assim: sempre que ele fizer X check-ins e X for igual à recorrência configurada, recebe a mensagem.',
      'Essa mensagem pode estar vinculada a um brinde ou ser apenas informativa.',
    ],
    tab: 2,
    target: '[data-tour="nivel-epico-card"]',
    scrollIntoView: true,
    panelPosition: 'bottom-left',
  });

  return steps;
};

const ClubeDashboard = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'configuracoes' ? 5 : 0);
  const [aberto, setAberto] = useState(null);
  const [restauranteConfig, setRestauranteConfig] = useState(null);
  const [onboarding, setOnboarding] = useState({ open: false, stepIndex: 0, steps: [] });
  const [forceConfigModal, setForceConfigModal] = useState(false);
  const [onboardingInitialized, setOnboardingInitialized] = useState(false);
  const navigate = useNavigate();

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  useEffect(() => {
    atualizarAberto();
  }, []);

  const hasLocalidadePrincipal = useMemo(() => {
    if (!restauranteConfig) return false;
    const hasLat = restauranteConfig.lat !== null && restauranteConfig.lat !== undefined && restauranteConfig.lat !== '';
    const hasLong = restauranteConfig.long !== null && restauranteConfig.long !== undefined && restauranteConfig.long !== '';
    const hasRaio = restauranteConfig.raio !== null && restauranteConfig.raio !== undefined && restauranteConfig.raio !== '';
    const hasIntervalo = restauranteConfig.intervalo_checkin !== null
      && restauranteConfig.intervalo_checkin !== undefined
      && restauranteConfig.intervalo_checkin !== '';
    return hasLat && hasLong && hasRaio && hasIntervalo;
  }, [restauranteConfig]);

  const atualizarAberto = () => {
    api
      .get('/checkin/aberto')
      .then((res) => setAberto(res.data))
      .catch(() => setAberto(null));
  };

  useEffect(() => {
    if (onboardingInitialized) return;
    api.get('/config/restaurante')
      .then((res) => {
        const config = res.data || {};
        setRestauranteConfig(config);
        if (config.primeiro_acesso) {
          const hasLocalidade = config.lat !== null
            && config.lat !== undefined
            && config.lat !== ''
            && config.long !== null
            && config.long !== undefined
            && config.long !== ''
            && config.raio !== null
            && config.raio !== undefined
            && config.raio !== ''
            && config.intervalo_checkin !== null
            && config.intervalo_checkin !== undefined
            && config.intervalo_checkin !== '';
          setOnboarding({
            open: true,
            stepIndex: 0,
            steps: buildOnboardingSteps(hasLocalidade),
          });
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar config do restaurante:', err);
      })
      .finally(() => setOnboardingInitialized(true));
  }, [onboardingInitialized]);

  useEffect(() => {
    if (!onboarding.open) return;
    const step = onboarding.steps[onboarding.stepIndex];
    if (step?.tab !== undefined && step.tab !== null && step.tab !== tab) {
      setTab(step.tab);
    }
    setForceConfigModal(Boolean(step?.openConfigModal));
  }, [onboarding.open, onboarding.stepIndex, onboarding.steps, tab]);

  const handleConfigSaved = (updated) => {
    setRestauranteConfig((prev) => ({ ...prev, ...updated }));
    const currentStep = onboarding.steps[onboarding.stepIndex];
    if (onboarding.open && currentStep?.id === 'localidade') {
      setOnboarding((prev) => {
        const nextIndex = Math.min(prev.stepIndex + 1, prev.steps.length - 1);
        return { ...prev, stepIndex: nextIndex };
      });
    }
  };

  const handleNextStep = () => {
    setOnboarding((prev) => {
      const nextIndex = Math.min(prev.stepIndex + 1, prev.steps.length - 1);
      return { ...prev, stepIndex: nextIndex };
    });
  };

  const handleBackStep = () => {
    setOnboarding((prev) => {
      const nextIndex = Math.max(prev.stepIndex - 1, 0);
      return { ...prev, stepIndex: nextIndex };
    });
  };

  const handleCloseOnboarding = () => {
    setOnboarding((prev) => ({ ...prev, open: false }));
    setForceConfigModal(false);
  };

  const handleFinishOnboarding = async () => {
    try {
      await api.put('/config/restaurante', { primeiro_acesso: false });
      setRestauranteConfig((prev) => ({ ...prev, primeiro_acesso: false }));
    } catch (err) {
      console.error('Erro ao finalizar onboarding:', err);
    }
    setOnboarding((prev) => ({ ...prev, open: false }));
    setForceConfigModal(false);
  };

  return (
    <ThemeProvider theme={themeClube}>
      <CssBaseline />
      <Box p={4} sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="relative"
          mb={3}
        >
          <IconButton
            onClick={() => navigate('/products')}
            sx={{ color: 'text.primary', position: 'absolute', left: { xs: 0, sm: 16 } }}
            aria-label="Voltar para produtos"
          >
            <ArrowBack />
          </IconButton>
          <img
            src="/clube.png"
            alt="Clube de Mesa"
            style={{ maxHeight: 120, objectFit: 'contain' }}
          />
        </Box>

        <Typography variant="h4" align="center" mb={2}>
          Painel de Controle
        </Typography>

        {aberto !== null && (
          <Box display="flex" justifyContent="center" mt={1} mb={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#fff',
                px: 2,
                py: 1,
                borderRadius: '8px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                gap: 1.5,
                border: `1px solid ${aberto ? '#a5d6a7' : '#ef9a9a'}`,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: aberto ? '#2e7d32' : '#c62828',
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.5)', opacity: 0.5 },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              />
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: aberto ? '#2e7d32' : '#c62828',
                  fontSize: '0.95rem',
                }}
              >
                {aberto ? 'Aberto agora' : 'Fechado no momento'}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Abas com scroll horizontal no mobile e centralização no desktop */}
        <Box
          sx={{
            overflowX: 'auto',
            position: 'relative',
            maskImage: {
              xs: 'linear-gradient(to right, black 95%, transparent)',
              md: 'none'
            },
            WebkitMaskImage: {
              xs: 'linear-gradient(to right, black 95%, transparent)',
              md: 'none'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'center' },
              minWidth: '600px',
            }}
          >
            <Tabs
              value={tab}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              textColor="secondary"
              indicatorColor="secondary"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: '#555',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#d30038',
                    borderBottom: '2px solid #d30038',
                    backgroundColor: '#fff0f3',
                    borderRadius: 2,
                  },
                  mx: 1.5,
                  px: 3,
                  py: 1,
                },
                '& .Mui-selected': {
                  color: '#d30038 !important',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#d30038',
                  height: 3,
                  borderRadius: 2,
                },
              }}
            >
              <Tab label="Dashboard" />
              <Tab label="Clientes" />
              <Tab label="Mensagens" />
              <Tab label="Brindes" />
              <Tab label="Histórico" />
              <Tab label="Configurações" />
            </Tabs>
          </Box>
        </Box>

        <Box mt={4}>
          {tab === 0 && <DashboardTab />}
          {tab === 1 && <ClientesTab />}
          {tab === 2 && (
            <MensagensTab
              onboardingStepId={onboarding.open ? onboarding.steps[onboarding.stepIndex]?.id : null}
            />
          )}
          {tab === 3 && <BrindesTab />}
          {tab === 4 && <HistoricoTab />}
          {tab === 5 && (
            <ConfiguracoesTab
              onHorarioChange={atualizarAberto}
              forceOpenConfigModal={forceConfigModal}
              onConfigModalClose={() => setForceConfigModal(false)}
              onConfigSaved={handleConfigSaved}
            />
          )}
        </Box>
        <OnboardingTour
          open={onboarding.open}
          steps={onboarding.steps}
          stepIndex={onboarding.stepIndex}
          onBack={handleBackStep}
          onNext={handleNextStep}
          onClose={handleCloseOnboarding}
          onFinish={handleFinishOnboarding}
          nextDisabled={onboarding.steps[onboarding.stepIndex]?.id === 'localidade' && !hasLocalidadePrincipal}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ClubeDashboard;
